// AT-API-020-H3 (doc 34/03 §7) -- the one-line wording of a source a tutor
// reply rests on (Tutor API 0.2.0 `citations`, migration reference §4), shared
// by the sources list under the reply (ChatSources.vue) and by the title of
// the `[^n]` markers in the text (MarkdownContent.vue).
//
// The parts are the fields the reply actually carries, in this order: the
// source's title; the page as the book prints it («صفحهٔ ۳۵»); the item the
// passage names -- the exercise or example -- as «set label» («تمرین ۲»),
// where `set` is the titled set the item stands in and, when the reply gives
// none, the kind's own word is used for a kind this client knows. Nothing is
// computed or invented: a part that is absent is simply not there, and a
// source with none of them is named by the neutral fallback («منبع بدون
// عنوان»), never by a made-up title. `t` is the page's own translator.

// The item kinds this client has a word for (`aiTutorSetup.chat.sources.kinds`).
const KNOWN_KINDS = ['exercise', 'example']

function itemText(item, t) {
  if (!item || typeof item !== 'object') return ''
  const set =
    (typeof item.set === 'string' && item.set) ||
    (KNOWN_KINDS.includes(item.kind)
      ? t(`aiTutorSetup.chat.sources.kinds.${item.kind}`)
      : '')
  const label = typeof item.label === 'string' ? item.label : ''
  return [set, label].filter(Boolean).join(' ')
}

/**
 * The parts of a source line that are present, in order (title, page, item).
 * @param {import('@/services/aiTutor/domain').TutorSource|null|undefined} source
 * @param {(key: string, values?: Object) => string} t
 * @returns {string[]}
 */
export function sourceLineParts(source, t) {
  const parts = []
  if (!source || typeof source !== 'object') return parts
  if (typeof source.title === 'string' && source.title) parts.push(source.title)
  if (typeof source.page === 'string' && source.page) {
    parts.push(t('aiTutorSetup.chat.sources.page', { page: source.page }))
  }
  const item = itemText(source.item, t)
  if (item) parts.push(item)
  return parts
}

/**
 * The whole line: the present parts joined by « · », or the neutral fallback
 * when the reply gave nothing to show for this source.
 * @param {import('@/services/aiTutor/domain').TutorSource|null|undefined} source
 * @param {(key: string, values?: Object) => string} t
 * @returns {string}
 */
export function sourceLine(source, t) {
  const parts = sourceLineParts(source, t)
  return parts.length
    ? parts.join(' · ')
    : t('aiTutorSetup.chat.sources.unnamed')
}

/**
 * 36_TUTOR_POST_UAT_CORRECTIONS TC-3 (doc 36/01 §4) -- the Sources rows: the
 * reply's per-`n` sources grouped for DISPLAY only. Citations of the same
 * source (`sourceId`, the provider's stable identity) on the same printed
 * page are one row; a citation without a reliable `sourceId` or page is its
 * own row -- never merged with another one by title. Each row keeps the
 * marker numbers it stands for (the provider's numbering, untouched). The
 * per-`n` list itself is not changed: the markers and the page viewer keep
 * reading it.
 *
 * Package 38 UX-2 (doc 38/00 §5): `openN` is the first marker number of the
 * row whose source carries a page image (`source.image`), or null. It is a
 * NUMBER only -- the card asks the list to open that marker's page in the
 * same viewer, through the same signed URL the marker uses; no URL or
 * identifier is copied into the row.
 * @param {import('@/services/aiTutor/domain').TutorSource[]} sources
 * @returns {{key: string, numbers: number[], openN: number|null, title: string|null, page: string|null, items: Object[]}[]}
 */
export function groupSources(sources) {
  const ordered = (Array.isArray(sources) ? sources : [])
    .filter(
      source =>
        source &&
        typeof source === 'object' &&
        Number.isInteger(source.n) &&
        source.n >= 1
    )
    .slice()
    .sort((a, b) => a.n - b.n)
  const groups = []
  ordered.forEach(source => {
    const identified =
      typeof source.sourceId === 'string' &&
      source.sourceId &&
      typeof source.page === 'string' &&
      source.page
    const key = identified
      ? JSON.stringify([source.sourceId, source.page])
      : `n:${source.n}`
    let group = groups.find(entry => entry.key === key)
    if (!group) {
      group = {
        key,
        numbers: [],
        openN: null,
        title: source.title || null,
        page: source.page || null,
        items: [],
      }
      groups.push(group)
    }
    if (!group.numbers.includes(source.n)) group.numbers.push(source.n)
    if (
      group.openN === null &&
      typeof source.image === 'string' &&
      source.image
    ) {
      group.openN = source.n
    }
    if (source.item) group.items.push(source.item)
  })
  return groups
}

/**
 * One group's card cells (V405 `.message-source-detail`). The «صفحه» cell
 * exists only when the reply gave a printed page -- it is never filled with
 * anything else (36 review P2-1): the page, followed by the distinct items
 * its citations name. Without a page those items join the book cell, after
 * the title (or the neutral fallback).
 * @param {{title: string|null, page: string|null, items: Object[]}} group
 * @param {(key: string, values?: Object) => string} t
 * @returns {{book: string, page: string}}
 */
export function sourceGroupCells(group, t) {
  const source = group && typeof group === 'object' ? group : {}
  const title =
    typeof source.title === 'string' && source.title
      ? source.title
      : t('aiTutorSetup.chat.sources.unnamed')
  const items = []
  ;(source.items || []).forEach(item => {
    const text = itemText(item, t)
    if (text && !items.includes(text)) items.push(text)
  })
  const page = typeof source.page === 'string' && source.page ? source.page : ''
  return page
    ? { book: title, page: [page].concat(items).join(' · ') }
    : { book: [title].concat(items).join(' · '), page: '' }
}
